/**
 * DevSimulatorButton
 *
 * Pulsante flottante SEMPRE VISIBILE in basso a sinistra (come il pulsante chat).
 * z-index alto per essere utilizzabile anche con modali aperte.
 *
 * Comportamento:
 * - Simulator OFF → click abilita (con conferma modale)
 * - Simulator ON + form registrato → click apre/chiude pannello
 * - Simulator ON + nessun form → click disabilita (con conferma modale)
 *
 * @author Salvo de Luca - Dialogicom s.r.l.s.
 * @version 2.0.0
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import { Beaker, X } from 'lucide-react';
import { useDevSimulator } from './DevSimulatorContext';

export const DevSimulatorButton: React.FC = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/mobile-officina')) return null;

  const {
    isEnabled,
    toggleEnabled,
    registeredForms,
    activeFormId,
    isPanelOpen,
    togglePanel
  } = useDevSimulator();

  const activeForm = activeFormId ? registeredForms.get(activeFormId) : null;
  const hasForm = registeredForms.size > 0;

  // Nascondo completamente il pulsante quando il simulator è OFF (no clutter in produzione).
  // Per riattivare: window.toggleDevSimulator() da console o localStorage.
  if (!isEnabled) return null;

  const handleClick = () => {
    if (!isEnabled) {
      // Simulatore OFF → abilita con conferma modale
      toggleEnabled();
    } else if (hasForm) {
      // ON con form → apri/chiudi pannello
      togglePanel();
    } else {
      // ON senza form → disabilita con conferma modale
      toggleEnabled();
    }
  };

  // Determina stile in base allo stato
  const getButtonStyle = () => {
    if (!isEnabled) {
      return 'bg-slate-700/80 text-slate-400 hover:bg-slate-600/80 hover:text-slate-300 border border-slate-600/50';
    }
    if (isPanelOpen) {
      return 'bg-red-600 hover:bg-red-500 text-white border border-red-500/50';
    }
    if (hasForm) {
      return 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white border border-amber-400/30 shadow-lg shadow-amber-500/20';
    }
    return 'bg-gradient-to-r from-amber-600/80 to-orange-600/80 hover:from-amber-500 hover:to-orange-500 text-amber-100 border border-amber-500/30';
  };

  return (
    <button
      onMouseDown={(e) => {
        // Previeni il blur degli input del form quando si clicca sul button
        e.preventDefault();
      }}
      onClick={handleClick}
      className={`
        fixed bottom-6 left-6 z-[9999]
        flex items-center gap-2
        px-4 py-3 rounded-full
        transition-all duration-300 ease-out
        hover:scale-105 active:scale-95
        ${getButtonStyle()}
      `}
      title={
        !isEnabled
          ? 'Abilita Dev Simulator'
          : isPanelOpen
            ? 'Chiudi simulatore'
            : hasForm
              ? `Simula: ${activeForm?.displayName || 'Form'}`
              : 'Disabilita Dev Simulator'
      }
    >
      {isPanelOpen ? (
        <>
          <X className="w-5 h-5" />
          <span className="font-semibold text-sm">Chiudi</span>
        </>
      ) : (
        <>
          <Beaker className={`w-5 h-5 ${isEnabled ? 'animate-pulse' : ''}`} />
          <span className="font-semibold text-sm">
            DEV {isEnabled ? 'ON' : 'OFF'}
          </span>
          {/* Badge con numero form se più di uno */}
          {hasForm && registeredForms.size > 1 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {registeredForms.size}
            </span>
          )}
          {/* Pallino verde quando form disponibile */}
          {hasForm && registeredForms.size === 1 && (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-slate-900" />
          )}
        </>
      )}
    </button>
  );
};

export default DevSimulatorButton;
